import type { GetStaticPaths, GetStaticProps } from "next";
import Layout from "../../components/layout";
import { MwSmallIcon } from "../../components/v2/icons";
import { careerJobs, CareerJob } from "../../lib/career-jobs";
import CareerApplyForm from "../../components/v2/form/career-apply";
import { getPageForSlug } from "../../lib/api";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/career.module.scss";
import "../../styles/scss/solutions.module.scss";

type CareerJobPageProps = {
    job: CareerJob;
    pageMeta: {
        isHomePage?: boolean;
        pageTitle: string;
        ogImage: string | null;
        uri: string;
        metaTitle: string;
        metaDesc: string;
        metaRobotsNoindex: string;
        metaRobotsNofollow: string;
    };
};

const CareerJobDetailPage = ({ job, pageMeta }: CareerJobPageProps) => {
    const plainJobTitle = job.title.replace(/<[^>]*>/g, "").trim();

    return (
        <Layout
            isHomePage={pageMeta?.isHomePage}
            pageTitle={job?.metaTitle}
            ogImage={`https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png`}
            uri={`/careers/${job?.slug}/`}
            metaTitle={job?.metaTitle}
            metaDesc={job?.metaDescription}
            metaRobotsNoindex="index"
            metaRobotsNofollow="index"
        >
            <div className="middle-wrapper site-page careerV2">
                <section className={`hero-section solutions-child-page`}>
                    <div className={'container'}>
                        <div className="section-tag testimonial pd-right"><MwSmallIcon /> Careers</div>
                        <h1 dangerouslySetInnerHTML={{ __html: job.title }} suppressHydrationWarning />
                        <div className="job-details">
                            <p>Location: {job.location}</p>
                            <p>Type: {job.type}</p>
                        </div>
                    </div>
                </section>
                <section className="job-description">
                    <div className="container">
                        <div className="job-description-content" suppressHydrationWarning>
                            <h2>About Middleware</h2>
                            <div dangerouslySetInnerHTML={{ __html: job.overview || "" }} suppressHydrationWarning />

                            <h2>Job overview</h2>
                            <div dangerouslySetInnerHTML={{ __html: job.jobOverview || "" }} suppressHydrationWarning />

                            <h2>Skills and Responsibilities</h2>
                            <ul>
                                {(job.skills || []).map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            <h2>Qualification and experience</h2>
                            <ul>
                                {(job.qualifications || []).map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>


                            <CareerApplyForm jobTitle={plainJobTitle} />

                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export const getStaticProps: GetStaticProps<CareerJobPageProps> = async ({ params, locale }) => {
    const selectedJobSlug = typeof params?.job === "string" ? params.job : "";
    const job = careerJobs.find((item) => item.slug === selectedJobSlug);

    if (!job) {
        return {
            notFound: true
        };
    }

    const data = await getPageForSlug("careers", "");
    const page = data?.pages?.edges?.[0]?.node;

    const metaTitle = job.metaTitle || `${job.title.replace(/<[^>]*>/g, "")} | Careers`;
    const description = job.metaDescription || `${job.title.replace(/<[^>]*>/g, "")} role details and application form.`;
    const uri = `/careers/${job.slug}/`;

    return {
        props: {
            job,
            pageMeta: {
                isHomePage: page?.isFrontPage || false,
                pageTitle: metaTitle,
                ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
                uri,
                metaTitle,
                metaDesc: description,
                metaRobotsNoindex: "index",
                metaRobotsNofollow: "follow"
            },
            metaTitle,
            description,
            ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
            uri,
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
            ...(await serverSideTranslations(locale ?? "en", ["common", "header", "footer"], nextI18NextConfig))
        }
    };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
    const paths = careerJobs.flatMap((job) => {
        if (locales && locales.length > 0) {
            return locales.map((locale) => ({
                params: { job: job.slug },
                locale
            }));
        }

        return [{ params: { job: job.slug } }];
    });

    return {
        paths,
        fallback: false
    };
};

export default CareerJobDetailPage;
